import React from "react";
import dayjs from "dayjs";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Chip,
  useTheme,
  Box,
  Tooltip,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { User } from "../../types/UserTypes";
import { useNavigate } from "react-router-dom";

type UserTableProps = {
  usersData: User[];
  maxHeight?: string | number;
  stickyHeader?: boolean;
  onSelectUser?: (user: User) => void;
};

const UserTable: React.FC<UserTableProps> = ({
  usersData,
  maxHeight = 400,
  stickyHeader = true,
  onSelectUser,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box>
      <TableContainer
        component={Paper}
        sx={{ borderRadius: 2, maxHeight: maxHeight, overflow: "auto" }}
      >
        <Table stickyHeader={stickyHeader}>
          <TableHead sx={{ backgroundColor: theme.palette.neutral["200"] }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.name")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.email")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.role")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.status")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.lastUpdate")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                {t("users.createdAt")}
              </TableCell>
              <TableCell sx={{ fontWeight: "bold", textAlign: "center" }}>
                {t("users.action")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {usersData.map((user, index) => (
              <TableRow
                key={index}
                onClick={() => {
                  navigate(`/users/profile/${user.id}`);
                  if (onSelectUser) {
                    onSelectUser(user);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  backgroundColor:
                    index % 2 === 1 ? theme.palette.neutral["100"] : "inherit",
                  transition: "background-color 0.3s ease",
                  "&:hover": {
                    backgroundColor: theme.palette.neutral["100"],
                  },
                }}
              >
                <TableCell>
                  <Typography variant="pSmall">{`${user.firstName} ${user.lastName}`}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="pSmall">{user.email}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="pSmall">{user.role}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={t(
                      `userStatus.${user.active ? "active" : "inactive"}`
                    )}
                    sx={{
                      backgroundColor: user.active
                        ? theme.palette.success.main
                        : theme.palette.neutral["400"],
                      color: "white",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="pSmallest">
                    {dayjs(user.updatedAt).isValid()
                      ? dayjs(user.updatedAt).format("MMM D, YYYY")
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="pSmallest">
                    {dayjs(user.createdAt).isValid()
                      ? dayjs(user.createdAt).format("MMM D, YYYY")
                      : "-"}
                  </Typography>
                </TableCell>
                <TableCell sx={{ textAlign: "center" }}>
                  <Box
                    sx={{ display: "flex", justifyContent: "center", gap: 1 }}
                  >
                    <Tooltip title={t("users.view")}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/users/profile/${user.id}`);
                        }}
                        size="small"
                        sx={{
                          color: theme.palette.info.main,
                          "&:hover": {
                            backgroundColor: `${theme.palette.info.light}20`,
                          },
                        }}
                      >
                        <VisibilityIcon sx={{ height: "20px" }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("users.edit")}>
                      <IconButton
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/users/edit/${user.id}`);
                        }}
                        size="small"
                        sx={{
                          color: theme.palette.primary.main,
                          "&:hover": {
                            backgroundColor: `${theme.palette.primary.light}20`,
                          },
                        }}
                      >
                        <EditIcon sx={{ height: "20px" }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default UserTable;
